import React, { useState, useEffect, useRef } from 'react';
import { FaAngleLeft, FaAngleRight, FaAngleDown } from 'react-icons/fa';
import { emptyDayDots } from '../util/mcpbPresence';

/**
 * MCCalendar — a small, dependency-free month-grid date picker for the mobile "Go to time" sheet.
 * The lite parity port of Java `MCCalendarPopup` (the month card): a 7×6 day grid with ◀ ▶ month
 * paging, a warm ring on today, a solid product-accent fill on the selected day, and a green
 * availability DOT on days that have recorded footage (fed by `onRequestPresence` → the
 * event.getEventPresence RPC at day granularity). Availability degrades to no-dots when the backend
 * lacks the RPC or a fetch fails — the grid stays a fully usable date picker (David 2026-08-04).
 *
 * Time-of-day is NOT chosen here (the sheet's time row owns HH:MM:SS); this returns a DATE only.
 * Timestamps are tz-naive local 'YYYYMMDD', same convention as mcpbTime/mcpbPresence.
 *
 * @param {object}   props
 * @param {string}   props.selectedYmd  'YYYYMMDD' of the currently-selected day (solid fill); seeds the
 *                                       opening month.
 * @param {string}   props.todayYmd     'YYYYMMDD' NVR "today" (warm ring); also caps selection — days
 *                                       after it are dimmed + non-clickable (no future playback jump).
 * @param {function} props.onPickDay    (ymd:'YYYYMMDD') => void — a day was tapped.
 * @param {function} [props.onRequestPresence] (year, month0) => Promise<{dots:boolean[32], unsupported}>
 *                                       — fetch the visible month's availability dots. Omit → no dots.
 */

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
// Sunday-first single-letter day-of-week headers — matches Java MCCalendarPopup DOW.
const DOW = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

const pad2 = (n) => ('0' + n).slice(-2);
const ymdOf = (year, month0, day) => `${year}${pad2(month0 + 1)}${pad2(day)}`;
const daysInMonth = (year, month0) => new Date(year, month0 + 1, 0).getDate();
const firstWeekday = (year, month0) => new Date(year, month0, 1).getDay(); // 0=Sun

/** Seed the opening month from a 'YYYYMMDD' (selected, else today), falling back to the real month. */
const seedView = (ymd, fallback) => {
  const src = (ymd && ymd.length >= 6) ? ymd : (fallback && fallback.length >= 6 ? fallback : '');
  if (src) return { year: +src.slice(0, 4), month0: +src.slice(4, 6) - 1 };
  const now = new Date();
  return { year: now.getFullYear(), month0: now.getMonth() };
};

/**
 * The 42 cells (6 weeks × 7 days) of a month grid: leading days from the previous month, this month's
 * 1..N, then trailing days from the next month, each tagged `inMonth`. Pure — exported for tests.
 */
export const monthGridCells = (year, month0) => {
  const cells = [];
  const lead = firstWeekday(year, month0);
  const dim = daysInMonth(year, month0);
  const prev = month0 === 0 ? { year: year - 1, month0: 11 } : { year, month0: month0 - 1 };
  const next = month0 === 11 ? { year: year + 1, month0: 0 } : { year, month0: month0 + 1 };
  const prevDim = daysInMonth(prev.year, prev.month0);
  for (let i = lead - 1; i >= 0; i -= 1) {
    const d = prevDim - i;
    cells.push({ year: prev.year, month0: prev.month0, day: d, inMonth: false });
  }
  for (let d = 1; d <= dim; d += 1) cells.push({ year, month0, day: d, inMonth: true });
  while (cells.length < 42) {
    const d = cells.length - (lead + dim) + 1;
    cells.push({ year: next.year, month0: next.month0, day: d, inMonth: false });
  }
  return cells;
};

const MCCalendar = ({ selectedYmd, todayYmd, onPickDay, onRequestPresence }) => {
  const [view, setView] = useState(() => seedView(selectedYmd, todayYmd));
  const [dots, setDots] = useState(emptyDayDots);
  // 'days' = the day grid; 'months' = the month picker (tap the title to jump months/years without
  // ◀-ing 12× — David 2026-08-04). The header controls (◀ ▶ + title) adapt to the mode.
  const [mode, setMode] = useState('days');

  // Keep the presence fetcher in a ref so the fetch effect depends ONLY on the visible month — a parent
  // that recreates the callback each render can't retrigger (or loop) the fetch.
  const reqRef = useRef(onRequestPresence);
  reqRef.current = onRequestPresence;
  // Stale-guard: a fast month-page can resolve out of order; only the latest request's dots land.
  const seqRef = useRef(0);

  useEffect(() => {
    if (mode !== 'days') return undefined; // month-picker navigation doesn't need day dots
    const fetcher = reqRef.current;
    setDots(emptyDayDots()); // clear immediately so a previous month's dots never linger while loading
    if (!fetcher) return undefined;
    const seq = (seqRef.current += 1);
    let alive = true;
    Promise.resolve(fetcher(view.year, view.month0)).then((r) => {
      if (!alive || seq !== seqRef.current) return; // superseded by a newer month
      setDots((r && r.dots) || emptyDayDots());
    }).catch(() => { if (alive && seq === seqRef.current) setDots(emptyDayDots()); });
    return () => { alive = false; };
  }, [view.year, view.month0, mode]);

  const stepMonth = (delta) => setView((v) => {
    const m = v.month0 + delta;
    if (m < 0) return { year: v.year - 1, month0: 11 };
    if (m > 11) return { year: v.year + 1, month0: 0 };
    return { year: v.year, month0: m };
  });
  const stepYear = (delta) => setView((v) => ({ year: v.year + delta, month0: v.month0 }));

  // Future cap at NVR "today": no navigating/selecting past it (no future footage). Absent todayYmd
  // (cloud) → no cap.
  const todayYear = todayYmd ? +todayYmd.slice(0, 4) : null;
  const todayMonth0 = todayYmd ? +todayYmd.slice(4, 6) - 1 : null;
  const nextMonthDisabled = todayYmd != null && (view.year > todayYear
    || (view.year === todayYear && view.month0 >= todayMonth0));
  const nextYearDisabled = todayYmd != null && view.year >= todayYear;

  const cells = monthGridCells(view.year, view.month0);

  return (
    <div className="mcpb-cal" role="group" aria-label="Select a date">
      <div className="mcpb-cal__head">
        <button
          className="mcpb-cal__nav"
          type="button"
          onClick={() => (mode === 'days' ? stepMonth(-1) : stepYear(-1))}
          aria-label={mode === 'days' ? 'Previous month' : 'Previous year'}
        >
          <FaAngleLeft aria-hidden="true" />
        </button>
        <button
          className="mcpb-cal__title"
          type="button"
          onClick={() => setMode((mo) => (mo === 'days' ? 'months' : 'days'))}
          aria-expanded={mode === 'months'}
          aria-label="Choose month and year"
        >
          {mode === 'days' ? `${MONTHS[view.month0]} ${view.year}` : `${view.year}`}
          <FaAngleDown className={`mcpb-cal__title-caret${mode === 'months' ? ' mcpb-cal__title-caret--open' : ''}`} aria-hidden="true" />
        </button>
        <button
          className="mcpb-cal__nav"
          type="button"
          onClick={() => (mode === 'days' ? stepMonth(1) : stepYear(1))}
          aria-label={mode === 'days' ? 'Next month' : 'Next year'}
          disabled={mode === 'days' ? nextMonthDisabled : nextYearDisabled}
        >
          <FaAngleRight aria-hidden="true" />
        </button>
      </div>

      {mode === 'months' ? (
        <div className="mcpb-cal__months" role="grid">
          {MONTHS_SHORT.map((mo, i) => {
            const isViewed = i === view.month0;
            const isFuture = todayYmd != null && (view.year > todayYear
              || (view.year === todayYear && i > todayMonth0));
            const cls = ['mcpb-cal__month'];
            if (isViewed) cls.push('mcpb-cal__month--selected');
            return (
              <button
                key={mo}
                type="button"
                className={cls.join(' ')}
                disabled={isFuture}
                aria-pressed={isViewed}
                onClick={() => { setView({ year: view.year, month0: i }); setMode('days'); }}
              >
                {mo}
              </button>
            );
          })}
        </div>
      ) : (
      <>
      <div className="mcpb-cal__dow" aria-hidden="true">
        {DOW.map((d, i) => <span key={i} className="mcpb-cal__dow-cell">{d}</span>)}
      </div>

      <div className="mcpb-cal__grid" role="grid">
        {cells.map((c, i) => {
          const ymd = ymdOf(c.year, c.month0, c.day);
          const isSelected = c.inMonth && ymd === selectedYmd;
          const isToday = c.inMonth && ymd === todayYmd;
          const isFuture = c.inMonth && !!todayYmd && ymd > todayYmd;
          const hasDot = c.inMonth && !!dots[c.day];
          const disabled = !c.inMonth || isFuture;
          const cls = ['mcpb-cal__day'];
          if (!c.inMonth) cls.push('mcpb-cal__day--adjacent');
          if (isFuture) cls.push('mcpb-cal__day--future');
          if (isToday) cls.push('mcpb-cal__day--today');
          if (isSelected) cls.push('mcpb-cal__day--selected');
          return (
            <button
              key={i}
              type="button"
              className={cls.join(' ')}
              disabled={disabled}
              aria-label={`${MONTHS_SHORT[c.month0]} ${c.day}, ${c.year}`}
              aria-pressed={isSelected}
              aria-current={isToday ? 'date' : undefined}
              onClick={disabled ? undefined : () => onPickDay(ymd)}
            >
              <span className="mcpb-cal__day-num">{c.day}</span>
              {hasDot && <span className="mcpb-cal__dot" aria-hidden="true" />}
            </button>
          );
        })}
      </div>
      </>
      )}
    </div>
  );
};

export default MCCalendar;
